import {
  isPermissionDrivenRole,
  isPermissionGranted,
  permissionValue,
} from '@strength/domain';
import type { PermissionGroupId } from '@strength/domain';
import z from 'zod';
import type { ZeroQueryContext } from '../auth-context';
import { defineOrgQuery, defineTeamQuery } from '../shared/procedures';

/**
 * Cross-team reads span every team in the org, so they run only for org-wide
 * sessions. The whitelist grants the names to every role with `plan` tRPC
 * access — the team restriction is carried here, in the definition, not in
 * the whitelist. Throwing surfaces as Zero's per-query app-error envelope;
 * no new error codes.
 */
function assertOrgWideSession(ctx: ZeroQueryContext): void {
  if (ctx.teamIds !== null) {
    throw new Error(
      'Insufficient permissions: team-restricted sessions cannot read cross-team data',
    );
  }
}

/**
 * SC-5685: the query definition's own view-permission guard, extending the
 * team-restriction pattern above. The whitelist already AND-gates these
 * queries by the governing Views for permission-driven roles
 * (permissionAccessEntries) — this re-checks the same leaf here so a
 * whitelist or permission-config drift cannot answer the query for a viewer
 * whose View is revoked. The document is the one the /zero/query handler
 * refreshed per request (getCachedRole, invalidated on save) — never the
 * session-baked snapshot. Only explicit `false` denies (Rule 3): legacy
 * roles and permission-driven viewers without a stored document pass.
 */
function assertViewPermission(
  ctx: ZeroQueryContext,
  group: PermissionGroupId,
): void {
  if (!isPermissionDrivenRole(ctx.role)) {
    return;
  }
  if (
    isPermissionGranted(permissionValue(ctx.permissions ?? null, group, 'view'))
  ) {
    return;
  }
  throw new Error(
    'Insufficient permissions: the governing view permission is revoked',
  );
}

const windowSchema = {
  startDate: z.string().date(),
  endDate: z.string().date(),
};

/**
 * Zero date columns compare as epoch-ms numbers on the wire; the read funnels
 * convert rows back with `new Date(ms).toISOString().slice(0, 10)`, so a
 * window bound is the UTC midnight of the ISO date argument.
 */
const dateToEpochMs = (isoDate: string): number => Date.parse(isoDate);

export const trackQueries = {
  tracksForTeam: defineTeamQuery(
    z.object({ teamIds: z.array(z.uuid()) }),
    ({ teamId, orgQuery }) =>
      orgQuery('Track')
        .where('teamId', '=', teamId)
        .where('deletedAt', 'IS', null)
        .related('athletes', (q) => q.where('deletedAt', 'IS', null)),
  ),
  /**
   * An athlete's active, non-deleted tracks on every team except the active
   * one, with each track's non-deleted blocks that overlap the window and,
   * nested in each block, the athlete's current published workout layers
   * whose assignedDate falls inside the window (flex workouts — null
   * assignedDate — are excluded, matching the tRPC cross-team endpoint).
   */
  crossTeamTracksForAthlete: defineOrgQuery(
    z.object({
      athleteProfileId: z.uuid(),
      activeTeamId: z.uuid(),
      ...windowSchema,
    }),
    ({ args, ctx, orgQuery }) => {
      assertOrgWideSession(ctx);
      // The route embeds nested blocks, so Blocks View governs it (the
      // permission config AND-gates the same leaf in the whitelist).
      assertViewPermission(ctx, 'plan.blocks');
      return orgQuery('Track')
        .where('deletedAt', 'IS', null)
        .where('archivedAt', 'IS', null)
        .where('teamId', '!=', args.activeTeamId)
        .whereExists('athletes', (q) =>
          q
            .where('profileId', '=', args.athleteProfileId)
            .where('deletedAt', 'IS', null),
        )
        .related('blocks', (q) =>
          q
            .where('deletedAt', 'IS', null)
            .where('startDate', '<=', dateToEpochMs(args.endDate))
            .where('endDate', '>=', dateToEpochMs(args.startDate))
            .related('athletePublishedWorkoutLayers', (lq) =>
              lq
                .where('profileId', '=', args.athleteProfileId)
                .where('isCurrent', '=', true)
                .where('assignedDate', '>=', dateToEpochMs(args.startDate))
                .where('assignedDate', '<=', dateToEpochMs(args.endDate)),
            ),
        );
    },
  ),
  /** The phases of the requested teams that overlap the window. */
  crossTeamPhases: defineOrgQuery(
    z.object({
      teamIds: z.array(z.uuid()).min(1),
      ...windowSchema,
    }),
    ({ args, ctx, orgQuery }) => {
      assertOrgWideSession(ctx);
      // Phases viewing — the permission config AND-gates the same leaf in
      // the whitelist.
      assertViewPermission(ctx, 'plan.phases');
      return orgQuery('Phase')
        .where('teamId', 'IN', args.teamIds)
        .where('startDate', '<=', dateToEpochMs(args.endDate))
        .where('endDate', '>=', dateToEpochMs(args.startDate));
    },
  ),
};
